import { useAccess } from "../lib/access.js";

/**
 * Sprint 23 Wave B (S23-B6) — Platform-Owner role helpers.
 *
 * Sprint 23 introduces a `platform.owner` JWT-claim role for the
 * Platform-Owner Backoffice surfaces in `apps/admin`. The role is
 * **distinct** from the tenant-scoped `tenant.admin` role:
 *
 *   - `tenant.admin` manages a single tenant (members, branding,
 *     audit log within the same tenant). These surfaces will move
 *     to the main app long-term per operator decision Q11; in
 *     Sprint 23 they remain in `apps/admin` with the audience
 *     banner that this module ships.
 *   - `platform.owner` manages cross-tenant platform operations
 *     (tenant CRUD in Wave B, managed hardware pool in Wave E,
 *     SIM inventory in Wave E).
 *
 * **Representation.** JWT-claim only — the operator adds the
 * literal string `"platform.owner"` to a user's `roles` claim in
 * Keycloak out-of-band. **No Keycloak realm-role change in Sprint
 * 23** (Q11). The realm-role version lands in Sprint 24 alongside
 * the login refactor (`docs/sprints/sprint-24/LOGIN_REBUILD_DESIGN.md`).
 */

/**
 * Resolves the platform-owner UI gate from the server-authoritative access
 * projection. The API remains the authorization boundary; this only controls
 * client visibility and fails closed while the projection is unavailable.
 */
export function usePlatformOwner(_session?: unknown): boolean {
  return useAccess().can("platform.tenants.write");
}
